/**
 * Knowledge files API client (M-X12 + M-X15).
 *
 * Four scopes hit the same RESTful shape:
 *   org    →  /orgs/:orgId/knowledge
 *   board  →  /projects/:projectId/knowledge
 *   role   →  /roles/:roleId/knowledge
 *   card   →  /cards/:cardId/knowledge        (M-X15)
 *
 * Uploads are multipart (FormData) so XHR is used directly for progress
 * support; everything else goes through apiJson. Cookies (JWT) flow
 * through because `apiFetch` sets `credentials: 'include'` and our
 * upload path mirrors that by setting `xhr.withCredentials = true`.
 *
 * Backend: apps/chaos-gateway/src/knowledge-files.ts
 */
import { env } from '../../env';
import { apiFetch, apiJson, apiUploadWithProgress } from './client';

export type KnowledgeScope = 'org' | 'board' | 'role' | 'card';

export interface KnowledgeFile {
  id: string;
  filename: string;
  /** Canonical host path, e.g. /srv/chaos/orgs/.../knowledge/<filename>.
   *  Used by the M-X35 "Open in Explorer" button to navigate the Explorer
   *  to this file's location. Null on rows inserted before M-X35. */
  filepath: string | null;
  sizeBytes: number;
  mimeType: string | null;
  description: string | null;
  uploadedAt: string;
  uploadedBy: string | null;
  /** W-UP async indexing: live KB status, CARD scope only (other scopes
   *  omit the field). `queued` means the embed worker hasn't finished —
   *  the tab polls the listing until every file leaves that state. */
  indexing?: UploadIndexing | null;
}

interface ListResponse {
  scope: KnowledgeScope;
  scope_id: string;
  canonical_dir: string;
  files: KnowledgeFile[];
}

/**
 * W-UP (2026-07-04): per-file KB indexing outcome, returned by the
 * gateway for CARD-scope uploads (other scopes carry no `indexing`
 * field). `status`:
 *   - queued       → chunk rows written; the background embed worker is
 *                    still producing vectors (chunks = total, embedded =
 *                    done so far). Poll the listing until it flips;
 *   - indexed      → ingested into kb_sources/kb_chunks (chunks/embedded
 *                    carry the real counts; outcome = created|updated|
 *                    unchanged);
 *   - not_indexed  → stored on disk but honestly NOT searchable
 *                    (reason: unsupported_format | too_large_to_index |
 *                    empty_file | image_describe_unavailable |
 *                    pdf_too_many_pages | pdf_no_text_layer |
 *                    pdf_extract_failed | indexing_reason_unknown).
 *   - failed       → stored on disk, ingest errored (reason = safe code);
 *   - unknown      → the server could not read live indexing status, so it
 *                    deliberately makes no searchable/not-searchable claim.
 *
 * W-IMG: `via = 'image-description'` marks a searchable text body produced by
 * Gemini Vision from an image upload (so the UI can say "via image description").
 */
export interface UploadIndexing {
  status: 'indexed' | 'queued' | 'not_indexed' | 'failed' | 'unknown';
  outcome?: string;
  chunks?: number;
  embedded?: number;
  reason?: string;
  via?: 'image-description';
}

interface UploadResponse {
  file: {
    filename: string;
    sizeBytes: number;
    mimeType: string | null;
    description: string | null;
  };
  indexing?: UploadIndexing;
}

export interface UploadResult {
  file: UploadResponse['file'];
  indexing: UploadIndexing | null;
}

interface UploadOptions {
  scope: KnowledgeScope;
  scopeId: string;
  /** ROLE scope: the active Workspace. See `scopeQuery`. */
  orgId?: string | null;
  file: File;
  description?: string;
  signal?: AbortSignal;
  onProgress?: (progress: { loaded: number; total: number }) => void;
}

function scopePath(scope: KnowledgeScope, scopeId: string): string {
  switch (scope) {
    case 'org':
      return `/orgs/${scopeId}/knowledge`;
    case 'board':
      return `/projects/${scopeId}/knowledge`;
    case 'role':
      return `/roles/${scopeId}/knowledge`;
    case 'card':
      // M-X15: card-level knowledge — per-task reference docs.
      return `/cards/${scopeId}/knowledge`;
  }
}

/**
 * ── WHICH WORKSPACE THIS ROLE UPLOAD BELONGS TO ───────────────────────
 *
 * Only the ROLE routes take one, and they REQUIRE it. The other three scopes
 * carry a Workspace in the resource itself (an org id, a project's org, a
 * card's org); a role does not — a global role is shared by every Workspace it
 * is published into, so the gateway cannot tell from the id alone where to
 * check the caller's rights, and it refuses to guess (400 `workspace_required`).
 * Sending the active Workspace is therefore not a hint, it is the question
 * being asked. Gateway side: `assertRoleKnowledgeAccess` in
 * apps/chaos-gateway/src/knowledge-files.ts.
 */
function scopeQuery(scope: KnowledgeScope, orgId?: string | null): string {
  if (scope !== 'role' || !orgId) return '';
  return `?orgId=${encodeURIComponent(orgId)}`;
}

export const knowledgeFilesApi = {
  list(
    scope: KnowledgeScope,
    scopeId: string,
    orgId?: string | null,
  ): Promise<ListResponse> {
    return apiJson<ListResponse>(`${scopePath(scope, scopeId)}${scopeQuery(scope, orgId)}`);
  },

  /**
   * Upload a knowledge file. Retries a transient failure with backoff and
   * reports byte progress; a genuine 4xx still fails fast with the gateway's
   * own error code so the message stays truthful.
   *
   * ONE code path, FOUR routes (org / board / role / card). All four inherit
   * the pre-send verified read added 2026-08-16, and all four needed it: the
   * session composer uploads picked attachments through here, and a File whose
   * backing file has moved reached the transport as a "network error" and was
   * re-sent three times. The permanent refusals this route can produce are
   * 413 `file_too_large` (25 MB) and 413 `storage_quota_exceeded` — the second
   * is the one that matters most, because retrying it three times tells a
   * person over a storage limit that their network is flaky.
   *
   * NOT added: a browser-side copy of the bytes. A KB document is a file the
   * uploader still holds; only lib/voice-upload.ts is the sole holder of what
   * it is sending, and only it carries the never-lose apparatus.
   */
  async upload(opts: UploadOptions): Promise<UploadResult> {
    const parsed = await apiUploadWithProgress<UploadResponse>(
      `${scopePath(opts.scope, opts.scopeId)}${scopeQuery(opts.scope, opts.orgId)}`,
      opts.file,
      {
        filename: opts.file.name,
        ...(opts.description ? { fields: { description: opts.description } } : {}),
        ...(opts.signal ? { signal: opts.signal } : {}),
        ...(opts.onProgress
          ? { onProgress: (p): void => opts.onProgress!({ loaded: p.loaded, total: p.total }) }
          : {}),
      },
    );
    return { file: parsed.file, indexing: parsed.indexing ?? null };
  },

  /**
   * URL that always SAVES the file. `?download=1` is explicit because the
   * route now serves audio/video `inline` so it can be played in place.
   */
  download(
    scope: KnowledgeScope,
    scopeId: string,
    filename: string,
    orgId?: string | null,
  ): string {
    // One query string, built once: the role routes now carry `orgId`, so
    // appending a second `?download=1` would produce a URL whose second
    // parameter the server never sees.
    const q = new URLSearchParams({ download: '1' });
    if (scope === 'role' && orgId) q.set('orgId', orgId);
    return `${knowledgeFilesApi.mediaUrl(scope, scopeId, filename)}?${q.toString()}`;
  },

  /**
   * URL that PLAYS the file in place — real MIME type, inline disposition and
   * HTTP Range support so the shared MediaPlayer can seek.
   */
  mediaUrl(
    scope: KnowledgeScope,
    scopeId: string,
    filename: string,
    orgId?: string | null,
  ): string {
    const base = env.VITE_API_BASE_URL.replace(/\/$/, '');
    return (
      `${base}${scopePath(scope, scopeId)}/${encodeURIComponent(filename)}` +
      scopeQuery(scope, orgId)
    );
  },

  async delete(
    scope: KnowledgeScope,
    scopeId: string,
    filename: string,
    orgId?: string | null,
  ): Promise<void> {
    await apiFetch(
      `${scopePath(scope, scopeId)}/${encodeURIComponent(filename)}${scopeQuery(scope, orgId)}`,
      { method: 'DELETE' },
    );
  },

  async updateDescription(
    scope: KnowledgeScope,
    scopeId: string,
    filename: string,
    description: string | null,
    orgId?: string | null,
  ): Promise<void> {
    await apiJson(
      `${scopePath(scope, scopeId)}/${encodeURIComponent(filename)}/description` +
        scopeQuery(scope, orgId),
      {
        method: 'POST',
        json: { description },
      },
    );
  },
};

export const knowledgeFilesKeys = {
  all: ['knowledge-files'] as const,
  /**
   * The Workspace is part of the key for ROLE scope. The same global role read
   * from two Workspaces is two authorization answers, and caching one under the
   * other's key would show a member a listing he was refused.
   */
  forScope: (scope: KnowledgeScope, scopeId: string, orgId?: string | null) =>
    [...knowledgeFilesKeys.all, scope, scopeId, scope === 'role' ? orgId ?? null : null] as const,
};
